import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  beginTenantWorkOSLogin,
  loadTenantIdentitySession,
  logoutTenantIdentitySession,
  refreshTenantIdentitySession,
  tenantIdentityRequest,
} from '../auth/tenantIdentityClient';

const TenantIdentityContext = createContext(null);

const tenantSessionTransitionError = () => {
  const error = new Error('Tenant sign out is in progress.');
  error.code = 'TENANT_AUTH_REQUIRED';
  error.status = 401;
  return error;
};

export const TenantIdentityProvider = ({ allowLegacyAuth0 = true, children }) => {
  const {
    getAccessTokenSilently,
    isAuthenticated: hasAuth0Session,
    isLoading: isAuth0Loading,
  } = useAuth0();
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  const refreshInFlightRef = useRef(null);
  const signOutInFlightRef = useRef(null);
  const signingOutRef = useRef(false);
  const identityGenerationRef = useRef(0);

  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  const commitSession = useCallback((nextSession) => {
    sessionRef.current = nextSession;
    setSession(nextSession);
  }, []);

  const load = useCallback(async () => {
    if (isAuth0Loading || signingOutRef.current) return null;
    const loadGeneration = identityGenerationRef.current + 1;
    identityGenerationRef.current = loadGeneration;
    setStatus('loading');
    setError(null);
    try {
      const nextSession = await loadTenantIdentitySession({
        getAccessTokenSilently: getAccessTokenSilentlyRef.current,
        hasAuth0Session: allowLegacyAuth0 && hasAuth0Session,
      });
      if (signingOutRef.current || identityGenerationRef.current !== loadGeneration) return null;
      commitSession(nextSession);
      setStatus('authenticated');
      return nextSession;
    } catch (nextError) {
      if (signingOutRef.current || identityGenerationRef.current !== loadGeneration) return null;
      commitSession(null);
      setError(nextError);
      setStatus(nextError.status === 401 ? 'anonymous' : 'error');
      return null;
    }
  }, [allowLegacyAuth0, commitSession, hasAuth0Session, isAuth0Loading]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshSession = useCallback(() => {
    if (signingOutRef.current) return Promise.reject(tenantSessionTransitionError());

    if (!refreshInFlightRef.current) {
      const refreshGeneration = identityGenerationRef.current;
      const refreshPromise = refreshTenantIdentitySession(
        sessionRef.current,
        getAccessTokenSilentlyRef.current,
      )
        .then((nextSession) => {
          if (signingOutRef.current || identityGenerationRef.current !== refreshGeneration) {
            throw tenantSessionTransitionError();
          }
          commitSession(nextSession);
          setStatus('authenticated');
          setError(null);
          return nextSession;
        })
        .finally(() => {
          if (refreshInFlightRef.current === refreshPromise) refreshInFlightRef.current = null;
        });
      refreshInFlightRef.current = refreshPromise;
    }
    return refreshInFlightRef.current;
  }, [commitSession]);

  const request = useCallback(async (endpoint, options) => {
    if (signingOutRef.current) throw tenantSessionTransitionError();
    const requestSession = sessionRef.current;
    try {
      return await tenantIdentityRequest(
        requestSession,
        getAccessTokenSilentlyRef.current,
        endpoint,
        options,
      );
    } catch (requestError) {
      if (requestError.code !== 'TENANT_SESSION_EXPIRED' || requestSession?.provider !== 'workos') {
        throw requestError;
      }

      const nextSession = await refreshSession();
      if (signingOutRef.current) throw tenantSessionTransitionError();
      return tenantIdentityRequest(
        nextSession,
        getAccessTokenSilentlyRef.current,
        endpoint,
        options,
      );
    }
  }, [refreshSession]);

  const refresh = refreshSession;

  const signOut = useCallback(({ beforeRedirect } = {}) => {
    if (signOutInFlightRef.current) return signOutInFlightRef.current;

    signingOutRef.current = true;
    identityGenerationRef.current += 1;
    const sessionAtSignOut = sessionRef.current;
    const signOutPromise = logoutTenantIdentitySession(
      sessionAtSignOut,
      getAccessTokenSilentlyRef.current,
    )
      .then(async (result) => {
        await beforeRedirect?.(result);
        commitSession(null);
        setStatus('anonymous');
        const destination = result?.logoutUrl || '/signin';
        window.location.assign(destination);
        return result;
      })
      .catch((logoutError) => {
        signingOutRef.current = false;
        setError(logoutError);
        throw logoutError;
      })
      .finally(() => {
        if (signOutInFlightRef.current === signOutPromise) signOutInFlightRef.current = null;
      });

    signOutInFlightRef.current = signOutPromise;
    return signOutPromise;
  }, [commitSession]);

  const value = useMemo(() => ({
    session,
    status,
    error,
    isAuthenticated: status === 'authenticated' && session?.authenticated === true,
    load,
    refresh,
    request,
    signIn: beginTenantWorkOSLogin,
    signOut,
  }), [error, load, refresh, request, session, signOut, status]);

  return <TenantIdentityContext.Provider value={value}>{children}</TenantIdentityContext.Provider>;
};

export const useTenantIdentity = () => {
  const value = useContext(TenantIdentityContext);
  if (!value) throw new Error('useTenantIdentity must be used inside TenantIdentityProvider');
  return value;
};

export const useOptionalTenantIdentity = () => useContext(TenantIdentityContext);

export default TenantIdentityContext;
